import React, { useState, useEffect } from 'react';
import { TrendingUp, Wallet, Users, Zap, Bell, Clock, ArrowUpRight } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { agenciesAPI } from '../utils/api';

const AgencyDashboard = () => {
  const [stats, setStats] = useState({
    monthly_recharge: 0,
    total_recharge: 0,
    recharge_times: 0,
    hosts_count: 0
  });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, historyRes] = await Promise.all([
        agenciesAPI.getDashboardStats(),
        agenciesAPI.getRechargeHistory()
      ]);
      setStats(statsRes.data);
      setHistory(historyRes.data.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setLoading(false);
    }
  };

  const chartData = history.slice(0, 7).reverse().map(item => ({
    name: new Date(item.created_at).toLocaleDateString('en-US', { weekday: 'short' }),
    value: parseFloat(item.amount)
  }));

  if (loading) return <div className="animate-pulse grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
    {[1,2,3,4].map(i => <div key={i} className="h-32 bg-white/5 rounded-[32px]"></div>)}
  </div>;

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'Monthly Volume', value: stats.monthly_recharge, icon: <TrendingUp />, color: 'from-pink-600 to-rose-600' },
          { label: 'Total Volume', value: stats.total_recharge, icon: <Wallet />, color: 'from-indigo-600 to-blue-600' },
          { label: 'Total Hosts', value: stats.hosts_count, icon: <Users />, color: 'from-amber-500 to-orange-500' },
          { label: 'Total Transactions', value: stats.recharge_times, icon: <Zap />, color: 'from-emerald-500 to-teal-500' },
        ].map((item, i) => (
          <div key={i} className="group bg-[#0f172a] border border-white/10 rounded-[32px] p-8 hover:border-white/20 transition-all relative overflow-hidden">
            <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${item.color} opacity-10 blur-3xl group-hover:opacity-20 transition-opacity`}></div>
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-12 h-12 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center text-white shadow-lg`}>
                {item.icon}
              </div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">{item.label}</p>
            </div>
            <h3 className="text-3xl font-black text-white tracking-tight">{item.value?.toLocaleString()}</h3>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Performance Chart */}
        <div className="lg:col-span-2 bg-[#0f172a] border border-white/10 rounded-[40px] p-10">
           <div className="flex items-center justify-between mb-10">
              <h3 className="text-2xl font-black text-white">Performance Overview</h3>
              <div className="flex items-center gap-2 text-pink-500 text-xs font-bold">
                 <ArrowUpRight size={16} />
                 <span>+12% vs last week</span>
              </div>
           </div>
           <div className="h-[350px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData.length ? chartData : [{ name: 'N/A', value: 0 }]}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#db2777" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#db2777" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff05" vertical={false} />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px' }}
                  />
                  <Area type="monotone" dataKey="value" stroke="#db2777" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                </AreaChart>
              </ResponsiveContainer>
           </div>
        </div>

        {/* Recent Activity Mini List */}
        <div className="bg-[#0f172a] border border-white/10 rounded-[40px] p-10">
           <h3 className="text-xl font-black text-white mb-8">Recent Recharges</h3>
           <div className="space-y-8">
              {history.slice(0, 5).map((item) => (
                <div key={item.id} className="flex items-center gap-4 group">
                   <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-slate-400 group-hover:bg-pink-500/10 group-hover:text-pink-500 transition-all">
                      <Clock size={18} />
                   </div>
                   <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white truncate">{item.user_name}</p>
                      <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{new Date(item.created_at).toLocaleTimeString()}</p>
                   </div>
                   <div className="text-right">
                      <p className="text-sm font-black text-white">+{parseFloat(item.amount).toLocaleString()}</p>
                      <p className="text-[10px] text-emerald-500 font-bold uppercase">Success</p>
                   </div>
                </div>
              ))}
              {history.length === 0 && <p className="text-center text-slate-600 font-bold py-10">No recent activity</p>}
           </div>
           <button className="w-full mt-10 py-5 bg-white/5 border border-white/10 rounded-[24px] text-sm font-bold text-slate-400 hover:bg-white/10 hover:text-white transition-all">
              View All Activity
           </button>
        </div>
    </div>
  </div>
  );
};

export default AgencyDashboard;
